import {startOfToday} from 'date-fns';
import {closeCurrentDialog, Form, FormField, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {
  CoreExchangeInspectionResultSchema,
  usePatchCoreExchangeInspectionMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, useGetUserOptions} from '@dms/shared';

import {getApiDateTimeString, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DialogActions} from './DialogActions';

const InspectionFormSchema = z.object({
  inspectedDate: z.date(),
  inspectedBy: z.string().min(1),
  inspectionResult: CoreExchangeInspectionResultSchema,
  additionalIdentifier: z.string(),
  coreInspectionNote: z.string(),
});

type InspectionForm = z.infer<typeof InspectionFormSchema>;

interface InspectionDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  currentUserId: string;
  onSaved: VoidFunction;
}

export function InspectionDialog(props: InspectionDialogProps) {
  const [patchInspection, {isLoading}] = usePatchCoreExchangeInspectionMutation();
  const {userOptions, isLoading: areUsersLoading} = useGetUserOptions();

  const inspectionResultOptions = [
    {
      value: CoreExchangeInspectionResultSchema.enum.PASSED,
      label: i18n.t('entity.recordAudit.taskVerdict.OK'),
    },
    {
      value: CoreExchangeInspectionResultSchema.enum.FAILED,
      label: i18n.t('entity.recordAudit.taskVerdict.BAD'),
    },
  ];

  const handleSubmit = async (values: InspectionForm) => {
    await patchInspection({
      coreExchangeId: props.coreExchangeId,
      body: {...values, inspectedDate: getApiDateTimeString(values.inspectedDate)},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<InspectionForm>
      experimentalZodSchema={InspectionFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        inspectedDate: startOfToday(),
        inspectedBy: props.currentUserId,
        inspectionResult: CoreExchangeInspectionResultSchema.enum.PASSED,
        additionalIdentifier: '',
        coreInspectionNote: '',
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="date"
            name="inspectedDate"
            label={i18n.t('entity.warehouse.labels.coreExchangeInspectionDate')}
            isRequired
            data-testid={suffixTestId('inputs.inspectedDate', props)}
          />
          <FormField
            control={control}
            type="choice"
            name="inspectedBy"
            label={i18n.t('entity.warehouse.labels.coreExchangeInspectedBy')}
            options={userOptions}
            isLoading={areUsersLoading}
            isNotClearable
            isRequired
            menuInPortal
            data-testid={suffixTestId('inputs.inspectedBy', props)}
          />
          <FormField
            control={control}
            type="radio"
            name="inspectionResult"
            label={i18n.t('entity.warehouse.labels.coreExchangeInspectionResult')}
            options={inspectionResultOptions}
            isRequired
            data-testid={suffixTestId('inputs.inspectionResult', props)}
          />
          <FormField
            control={control}
            type="text"
            name="additionalIdentifier"
            label={i18n.t('entity.warehouse.labels.coreExchangeAdditionalIdentifier')}
            data-testid={suffixTestId('inputs.additionalIdentifier', props)}
          />
          <FormField
            control={control}
            type="textarea"
            name="coreInspectionNote"
            label={i18n.t('entity.warehouse.labels.note')}
            data-testid={suffixTestId('inputs.coreInspectionNote', props)}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}
